/**
 * エクスポートユーティリティ関数
 */

/**
 * CanvasをPNG画像としてダウンロード
 */
export function downloadCanvasAsPNG(
  canvas: HTMLCanvasElement,
  filename: string = 'image.png'
): void {
  try {
    canvas.toBlob((blob) => {
      if (!blob) {
        console.error('Failed to create blob from canvas');
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 'image/png');
  } catch (error) {
    console.error('Failed to download canvas as PNG:', error);
  }
}

/**
 * CanvasをJPEG画像としてダウンロード
 */
export function downloadCanvasAsJPEG(
  canvas: HTMLCanvasElement,
  filename: string = 'image.jpg',
  quality: number = 0.9
): void {
  try {
    canvas.toBlob((blob) => {
      if (!blob) {
        console.error('Failed to create blob from canvas');
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 'image/jpeg', quality);
  } catch (error) {
    console.error('Failed to download canvas as JPEG:', error);
  }
}

/**
 * CanvasをSVGとしてダウンロード（簡易版：CanvasをSVGに変換）
 */
export function downloadCanvasAsSVG(
  canvas: HTMLCanvasElement,
  filename: string = 'image.svg'
): void {
  try {
    const dataURL = canvas.toDataURL('image/png');
    const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${canvas.width}" height="${canvas.height}">
  <image width="${canvas.width}" height="${canvas.height}" xlink:href="${dataURL}"/>
</svg>`;
    
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to download canvas as SVG:', error);
  }
}

/**
 * CanvasをPDFとしてダウンロード（jsPDFを使用）
 * 注意: jsPDFがインストールされている必要があります
 */
export async function downloadCanvasAsPDF(
  canvas: HTMLCanvasElement,
  filename: string = 'image.pdf',
  title?: string
): Promise<void> {
  try {
    // 動的インポート（jsPDFが利用可能な場合）
    const { default: jsPDF } = await import('jspdf');
    
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
      unit: 'mm',
      format: [canvas.width * 0.264583, canvas.height * 0.264583], // px to mm
    });

    if (title) {
      pdf.setFontSize(16);
      pdf.text(title, 10, 10);
    }

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
    const imgWidthInMM = imgWidth * ratio * 0.264583;
    const imgHeightInMM = imgHeight * ratio * 0.264583;
    const x = (pdfWidth - imgWidthInMM) / 2;
    const y = title ? 20 : (pdfHeight - imgHeightInMM) / 2;

    pdf.addImage(imgData, 'PNG', x, y, imgWidthInMM, imgHeightInMM);
    pdf.save(filename);
  } catch (error) {
    console.error('Failed to download canvas as PDF:', error);
    // フォールバック: PNGとしてダウンロード
    downloadCanvasAsPNG(canvas, filename.replace('.pdf', '.png'));
  }
}

/**
 * 共有リンクを生成（簡易版：データをBase64エンコードしてURLパラメータに含める）
 */
export function generateShareLink(data: any, type: string): string {
  try {
    const encoded = btoa(JSON.stringify(data));
    const baseUrl = window.location.origin + window.location.pathname;
    return `${baseUrl}?share=${type}&data=${encoded}`;
  } catch (error) {
    console.error('Failed to generate share link:', error);
    return '';
  }
}

/**
 * 共有リンクをクリップボードにコピー
 */
export async function copyShareLinkToClipboard(link: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(link);
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    // フォールバック: テキストエリアを使用
    const textarea = document.createElement('textarea');
    textarea.value = link;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      document.body.removeChild(textarea);
      return true;
    } catch (e) {
      document.body.removeChild(textarea);
      return false;
    }
  }
}

/**
 * HTML要素をPNG画像としてダウンロード（html2canvasを使用）
 */
export async function downloadElementAsPNG(
  element: HTMLElement,
  filename: string = 'image.png'
): Promise<void> {
  try {
    const html2canvas = (await import('html2canvas')).default;
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
    });
    downloadCanvasAsPNG(canvas, filename);
  } catch (error) {
    console.error('Failed to download element as PNG:', error);
  }
}

/**
 * HTML要素をPDFとしてダウンロード（html2canvas + jsPDFを使用）
 */
export async function downloadElementAsPDF(
  element: HTMLElement,
  filename: string = 'document.pdf',
  title?: string
): Promise<void> {
  try {
    const html2canvas = (await import('html2canvas')).default;
    const { default: jsPDF } = await import('jspdf');
    
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    if (title) {
      pdf.setFontSize(16);
      pdf.text(title, 10, 10);
    }

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = Math.min((pdfWidth - 20) / imgWidth, (pdfHeight - (title ? 30 : 20)) / imgHeight);
    const imgWidthInMM = imgWidth * ratio * 0.264583;
    const imgHeightInMM = imgHeight * ratio * 0.264583;
    const x = (pdfWidth - imgWidthInMM) / 2;
    const y = title ? 20 : (pdfHeight - imgHeightInMM) / 2;

    pdf.addImage(imgData, 'PNG', x, y, imgWidthInMM, imgHeightInMM);
    pdf.save(filename);
  } catch (error) {
    console.error('Failed to download element as PDF:', error);
    // フォールバック: PNGとしてダウンロード
    await downloadElementAsPNG(element, filename.replace('.pdf', '.png'));
  }
}

/**
 * 画面表示用（width/height が %）の SVG を、ラスタライズ・ファイル保存用に論理ピクセル寸法へ揃える
 */
export function normalizeSvgDimensionsForExport(svgElement: SVGSVGElement): void {
  const vb = svgElement.getAttribute('viewBox');
  if (!vb) {
    return;
  }
  const parts = vb
    .trim()
    .split(/[\s,]+/)
    .filter((s) => s.length > 0);
  if (parts.length !== 4) {
    return;
  }
  const w = parseFloat(parts[2]!);
  const h = parseFloat(parts[3]!);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return;
  }
  const wAttr = svgElement.getAttribute('width');
  const hAttr = svgElement.getAttribute('height');
  const needsW = !wAttr || /%/.test(wAttr);
  const needsH = !hAttr || /%/.test(hAttr);
  if (needsW) {
    svgElement.setAttribute('width', String(w));
  }
  if (needsH) {
    svgElement.setAttribute('height', String(h));
  }
}

/**
 * SVG要素をそのままSVGファイルとしてダウンロード
 */
export function downloadSVGElement(
  svgElement: SVGSVGElement,
  filename: string = 'image.svg'
): void {
  try {
    const clone = svgElement.cloneNode(true) as SVGSVGElement;
    normalizeSvgDimensionsForExport(clone);
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(clone);
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to download SVG element:', error);
  }
}

/**
 * SVG を PNG にラスタライズしてクリップボードへコピー（Word / PowerPoint / Slack 等は画像貼り付けを想定）
 */
export async function copySvgElementAsPngToClipboard(svgElement: SVGSVGElement): Promise<boolean> {
  if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
    return false;
  }
  try {
    const serializer = new XMLSerializer();
    const clone = svgElement.cloneNode(true) as SVGSVGElement;
    if (!clone.getAttribute('xmlns')) {
      clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    }
    normalizeSvgDimensionsForExport(clone);
    const svgString = serializer.serializeToString(clone);
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    try {
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('SVG rasterize failed'));
        img.src = url;
      });
    } finally {
      URL.revokeObjectURL(url);
    }

    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) return false;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    canvas.width = w;
    canvas.height = h;
    ctx.drawImage(img, 0, 0);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/png');
    });
    if (!blob) return false;

    await navigator.clipboard.write([
      new ClipboardItem({
        'image/png': blob,
      }),
    ]);
    return true;
  } catch (error) {
    console.error('Failed to copy SVG as PNG to clipboard:', error);
    return false;
  }
}

/**
 * HTML要素をPNG画像としてクリップボードにコピー
 */
export async function copyElementAsImageToClipboard(
  element: HTMLElement
): Promise<boolean> {
  try {
    const html2canvas = (await import('html2canvas')).default;
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
    });

    canvas.toBlob(async (blob) => {
      if (!blob) {
        console.error('Failed to create blob from canvas');
        return;
      }

      try {
        await navigator.clipboard.write([
          new ClipboardItem({
            'image/png': blob,
          }),
        ]);
      } catch (error) {
        console.error('Failed to copy image to clipboard:', error);
      }
    }, 'image/png');

    return true;
  } catch (error) {
    console.error('Failed to copy element as image to clipboard:', error);
    return false;
  }
}

